import { fetchPreparationMarkets } from '@morfi/contract-sdk';
import type { Connection, PublicKey } from '@solana/web3.js';
import type { TxEngineLogger } from '../tx-engine/config.js';
import { hashDiagnosticEntity } from '../tx-engine/diagnostics/lifecycle.js';

/**
 * Confirmed-account proof that `create_market` landed. The first positive read
 * is latched: the retry loop and the caller share one proof, so a load-balanced
 * RPC answering the later `confirmed` read from a lagging node cannot turn a
 * landed step back into a failure. A read failure counts as no proof.
 */
export function createShellLandingProof(
  connection: Connection,
  market: PublicKey,
  count: number,
  logger: TxEngineLogger | undefined,
): () => Promise<boolean> {
  let landed = false;
  return async () => {
    if (!landed) landed = await readShellLanded(connection, market, count, logger);
    return landed;
  };
}

async function readShellLanded(
  connection: Connection,
  market: PublicKey,
  count: number,
  logger: TxEngineLogger | undefined,
): Promise<boolean> {
  try {
    const snapshot = (await fetchPreparationMarkets(connection, [market]))[0];
    const landed =
      snapshot?.account !== null &&
      snapshot?.account !== undefined &&
      snapshot.account.nOutcomes >= count;
    if (landed)
      logger?.warn('market_creation.shell_landed_by_recovery', {
        marketHash: hashDiagnosticEntity(market.toBase58()),
        contextSlot: snapshot.slot,
        proofSource: 'confirmed_market_account',
      });
    return landed;
  } catch {
    logger?.warn('market_creation.shell_proof_read_failed', {
      marketHash: hashDiagnosticEntity(market.toBase58()),
    });
    return false;
  }
}

/**
 * Signature of the landed `create_market`. Display-only, so a lagging signature
 * index or an RPC error yields null instead of failing a flow whose account
 * proof already holds.
 */
export async function landedShellSignature(
  connection: Connection,
  market: PublicKey,
  logger: TxEngineLogger | undefined,
): Promise<string | null> {
  try {
    const entries = await connection.getSignaturesForAddress(market, { limit: 10 }, 'confirmed');
    // shortcut: newest successful tx on the market is create_market until outcomes are prepared (staging txs are older); a resumed run past that point reports a prepare signature — inspect instructions if this field gains a reader.
    const signature = entries.find((entry) => entry.err === null)?.signature;
    if (signature !== undefined) return signature;
  } catch {
    // Falls through to the unavailable log below.
  }
  logger?.warn('market_creation.shell_signature_unavailable', {
    marketHash: hashDiagnosticEntity(market.toBase58()),
  });
  return null;
}

/**
 * Prepared-outcome bitmap proven on chain so far. Bits only accumulate, so a
 * later `confirmed` read from a lagging node cannot drop an outcome an earlier
 * read already proved. Recovery is logged once, when a read first proves bits.
 */
export function createPreparedBitmapProof(
  connection: Connection,
  market: PublicKey,
  count: number,
  logger: TxEngineLogger | undefined,
): () => Promise<number> {
  let known = 0;
  return async () => {
    try {
      const snapshot = (await fetchPreparationMarkets(connection, [market]))[0];
      if (
        snapshot?.account === null ||
        snapshot?.account === undefined ||
        snapshot.account.nOutcomes < count
      )
        return known;
      const bitmap = snapshot.account.preparedOutcomesBitmap;
      if ((bitmap & ~known) !== 0)
        logger?.warn('market_creation.prepared_by_recovery', {
          marketHash: hashDiagnosticEntity(market.toBase58()),
          preparedBitmap: bitmap,
          contextSlot: snapshot.slot,
          proofSource: 'confirmed_market_account',
        });
      known |= bitmap;
      return known;
    } catch {
      logger?.warn('market_creation.preparation_proof_read_failed', {
        marketHash: hashDiagnosticEntity(market.toBase58()),
      });
      return known;
    }
  };
}
